import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {Pin as PlanPin} from '@mydocma/tile-viewer/lib/pin/pin';
import {Pin as BimPin} from '../../../bim-viewer/src/lib/models/pin';
import {MmBimService} from '../../mm-bim.service';
import {UntilDestroy} from '@ngneat/until-destroy';
import {MmFaultsLoaderService} from '../../../mm-faults/mm-faults-loader.service';
import {FaultPin} from '@docma-mm-core/plan/plan';
import {BpPinLocation, BpPinLocationTargetEnum} from '../../../bp-common/models/bp-pin';
import {MarkerIconTypeEnum} from '@mydocma/tile-viewer';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {MmFaultsViewService} from '../../../mm-faults/mm-faults-view/services/mm-faults-view.service';

export type PinId = string;
export type BimAndPlanPin = { bimPin: BimPin, planPin: PlanPin, id: string };
export type PlaceOfSelection = 'selectedOnPlan' | 'selectedOnModel';

@UntilDestroy()
@Injectable()
export class PinService {
  private pinsVisibilitySubject = new BehaviorSubject(true);
  public pinsVisibility$ = this.pinsVisibilitySubject.asObservable();

  private cachedPins: Map<PinId, BimAndPlanPin> = new Map();
  private pinsSubject = new BehaviorSubject<Map<PinId, BimAndPlanPin>>(new Map());

  public pins$ = this.pinsSubject.asObservable();

  private selectedPinSubject = new BehaviorSubject<BimAndPlanPin>(null);
  public selectedPin$ = this.selectedPinSubject.asObservable();

  constructor(private mmBimService: MmBimService,
              private faultsLoaderService: MmFaultsLoaderService,
              private deviceService: DeviceService,
              private readonly faultsViewService: MmFaultsViewService) {

  }

  get pinsVisibility(): boolean {
    return this.pinsVisibilitySubject.value;
  }

  get planPins(): PlanPin[] {
    const pins = [];
    this.pinsSubject.value?.forEach(value => {
      pins.push(value.planPin);
    });
    return pins;
  }



  getPinById(id: PinId): BimAndPlanPin {
    return this.pinsSubject.value.get(id);
  }

  public showExistingPins(): void {
    if (!this.pinsVisibility) {
      this.pinsSubject.next(new Map(this.cachedPins));
      this.pinsVisibilitySubject.next(true);
    }
  }
  public hideExistingPins(): void {
    if (this.pinsVisibility) {

      this.cachedPins = new Map(this.pinsSubject.value);
      this.pinsSubject.next(new Map());
      this.pinsVisibilitySubject.next(false);
    }
  }

// in order to have quick access to bimAndPlanPin we set  bimAndPlanPinId for both planPin and bimPin,
  public async setSelectedPlanPins(faultPins: FaultPin[]) {
    const pins = this.getPins(faultPins);

    const pinsMap = new Map(pins.map(pin => [pin.id, pin]));
    if (this.pinsVisibility) {
      this.pinsSubject.next(pinsMap);
      const currentFault = await firstValueFrom(this.faultsViewService.currentFault);
      if(currentFault) {
        this.selectPin(currentFault.bpPin.ExternalId);
      } else {
        this.selectedPinSubject.next(null);
      }
    } else {
      this.cachedPins = pinsMap;
    }
  }

  public async selectPin(pin: BimPin | PlanPin | string) {
    if (!pin) {
      this.selectedPinSubject.next(null);
      return;
    }

    let bimAndPlanPin: BimAndPlanPin;

    // Find pin by externalId after bim fault creation (MMW-1651)
    if(typeof pin === "string") {
      bimAndPlanPin = (await firstValueFrom(this.pins$)).get(pin);
    } else {
      bimAndPlanPin = this.pinsSubject.value.get(pin?.id);
    }

    this.selectedPinSubject.next(bimAndPlanPin);
  }

  private getPins(pins: FaultPin[]): BimAndPlanPin[] {


    return pins?.map(p => {
      // if (p.bpPin?.Id === '66b9b80c979b9b3ad97c135e') {
      //   console.log(p.bpPin, 'TEST-bpPin');
      // }

      let planLocation: BpPinLocation = null;
      let bimLocation: BpPinLocation = null;

      p.bpPin?.Locations?.forEach(l => {
        switch (l.Type) {
          case (BpPinLocationTargetEnum.plan):
            planLocation = l;
            break;
          case (BpPinLocationTargetEnum.bim):
            bimLocation = l;
            break;
        }
      });
      return {
        id: p.bpPin?.ExternalId,
        planPin: {
          id: p.bpPin?.ExternalId,
          data: p.fault,
          anchor: {x: planLocation?.X || p.coordinate.PosX, y: planLocation?.Y || p.coordinate.PosY},
          markerAnchor: (planLocation?.Marker?.Location ? {x: planLocation.Marker.Location.X, y: planLocation.Marker.Location.Y} : undefined),
          popupComponent: (!this.deviceService.isMobil() && !this.deviceService.isTablet() ? this.faultsViewService.popupComponent : undefined),  //MmFaultTileExtendedComponent
          readonly: true,
          markerOptions: {iconType: bimLocation ? MarkerIconTypeEnum.defaultWithObject : MarkerIconTypeEnum.default}
        },
        bimPin: !!p.bpPin ?
        {
          id: p.bpPin.ExternalId,
          data: p.fault,
          bimElementId: bimLocation?.AdditionalData?.bimElementId,
          camera: bimLocation?.AdditionalData?.camera,
          draggable: false,
          point: {x: bimLocation?.X, y: bimLocation?.Y, z: bimLocation?.Z},
          markerOptions: {
            iconType: 'default'
          },
          popupComponent: (!this.deviceService.isMobil() && !this.deviceService.isTablet() ? this.faultsViewService.popupComponent : undefined)
        } : null,
      } as BimAndPlanPin;
    });

    // if (planId === "653a5a177d89c89706a3c47c") {
    //   return of(pins);
    // } else if (planId === "653a56c07d89c89706a3c431") {
    //   return of(pins2);
    // }


  }

}
